import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from './components/Header';
import LocationDisplay from './components/LocationDisplay';
import InstructionsModal from './components/InstructionsModal';
import { convertCoordinates, exportToCsv } from './utils/helpers';
import { MapPin } from 'lucide-react';

const App = () => {
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [selectedCoordSystem, setSelectedCoordSystem] = useState(() => {
    // Intentar cargar la última selección del almacenamiento local
    const savedSystem = localStorage.getItem('selectedCoordSystem');
    return savedSystem || 'WGS84'; // Valor por defecto si no hay nada guardado
  });

  // Guardar la selección del sistema de coordenadas cada vez que cambie
  useEffect(() => {
    localStorage.setItem('selectedCoordSystem', selectedCoordSystem);
  }, [selectedCoordSystem]);

  const handleGetLocation = () => {
    setIsLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('Tu navegador es tan viejo que no sabe dónde está. ¡Actualízalo!');
      setIsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, altitude, accuracy } = position.coords;
        const source = position.coords.speed !== null ? 'GPS' : 'Antena'; // Simplistic check
        const newLocation = convertCoordinates(latitude, longitude, altitude, source, accuracy, selectedCoordSystem);
        setLocations((prevLocations) => [newLocation, ...prevLocations]);
        setIsLoading(false);
      },
      (err) => {
        let errorMessage = 'Error al obtener la ubicación. ¿Será que no quieres que te encontremos?';
        switch (err.code) {
          case err.PERMISSION_DENIED:
            errorMessage = 'Permiso denegado. ¡No podemos encontrarte si no nos dejas!';
            break;
          case err.POSITION_UNAVAILABLE:
            errorMessage = 'Ubicación no disponible. ¿Estás en un búnker?';
            break;
          case err.TIMEOUT:
            errorMessage = 'Tiempo de espera agotado. ¡Muévete más rápido!';
            break;
          default:
            break;
        }
        setError(errorMessage);
        setIsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleDeleteLocation = (id) => {
    setLocations((prevLocations) => prevLocations.filter((loc) => loc.id !== id));
  };

  const handleExportLocations = () => {
    exportToCsv(locations, 'mis_ubicaciones.csv');
  };

  const handleClearAllLocations = () => {
    setLocations([]);
  };

  const handleTitleClick = () => {
    window.open('https://www.topografia.com.es', '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 sm:p-8">
      <div className="container mx-auto max-w-5xl">
        <Header onTitleClick={handleTitleClick} />

        <motion.div
          className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-b-3xl p-6 shadow-xl -mt-4 relative z-10"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <motion.button
            onClick={handleGetLocation}
            className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl text-white font-bold text-lg shadow-lg transition-all duration-300 ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 hover:scale-105'
            }`}
            whileHover={isLoading ? {} : { scale: 1.02 }}
            whileTap={isLoading ? {} : { scale: 0.98 }}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Buscando...
              </>
            ) : (
              <>
                <MapPin className="w-6 h-6" />
                Obtener Ubicación
              </>
            )}
          </motion.button>

          {error && (
            <motion.p
              className="text-red-600 text-center mt-4 font-medium"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </motion.p>
          )}
        </motion.div>

        <LocationDisplay
          locations={locations}
          onDelete={handleDeleteLocation}
          onExport={handleExportLocations}
          onClear={handleClearAllLocations}
          onShowInstructions={() => setShowInstructions(true)}
          selectedCoordSystem={selectedCoordSystem}
          onCoordSystemChange={setSelectedCoordSystem}
        />

        <motion.footer
          className="text-center mt-8 text-gray-500 text-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.5 }}
        >
          <a
            href="https://www.topodron3d.com" // Asumiendo que Topodron3d tiene una web
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-600 transition-colors duration-200"
          >
            Realizado por Topodron3d + IA lerd
          </a>
        </motion.footer>
      </div>

      <InstructionsModal isOpen={showInstructions} onClose={() => setShowInstructions(false)} />
    </div>
  );
};

export default App;